import { useState, useMemo, useEffect, useRef } from "react";
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Plus, Percent, Trash2, Copy } from "lucide-react";
import { apiFetch } from "@/lib/api";
import BarcodeScanner from "@/components/BarcodeScanner";
import {
  defaultDuplicateFormState,
  type DuplicateFormState,
  type DuplicateProductOptions,
} from "@/lib/duplicateProductOptions";
import { Button } from "@/components/ui/button";
import BulkDiscountPanel from "@/components/admin/BulkDiscountPanel";
import ProductsSearch from "@/components/admin/ProductsSearch";
import ProductsTable from "@/components/admin/ProductsTable";
import DuplicateProductDialog from "@/components/admin/DuplicateProductDialog";
import DeleteConfirmDialog from "@/components/admin/DeleteConfirmDialog";
import StickerPrintControls from "@/components/admin/StickerPrintControls";

interface Category {
  id: number;
  name: string;
}

interface Product {
  id: number;
  name: string;
  sku: string | null;
  price: number;
  sale_price: number | null;
  condition: number;
  is_new: number;
  quantity: number;
  category_id: number | null;
  categoryName: string | null;
  size: string | null;
  bin_location: string | null;
  image: string | null;
}

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export default function Products() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [inStockOnly, setInStockOnly] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [showDiscount, setShowDiscount] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [duplicateOptions, setDuplicateOptions] = useState<DuplicateFormState>(defaultDuplicateFormState);
  const [mobileDuplicateProduct, setMobileDuplicateProduct] = useState<Product | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(null);
  const [discountPercent, setDiscountPercent] = useState("10");
  const [roundTo, setRoundTo] = useState<"none" | "floor" | "ceil">("floor");
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerError, setScannerError] = useState<string | null>(null);
  const [printMessage, setPrintMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [headerPrintQuantity, setHeaderPrintQuantity] = useState(1);

  const debouncedSearch = useDebounce(searchQuery, 300);

  const {
    data,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["admin-products", debouncedSearch, categoryFilter, inStockOnly],
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams();
      if (pageParam) params.set("cursor", pageParam);
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (categoryFilter !== "all") params.set("categoryId", categoryFilter);
      if (inStockOnly) params.set("inStock", "1");
      return apiFetch<{ products: Product[]; nextCursor: string | null }>(
        `/api/admin/products?${params.toString()}`
      );
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });

  const products = useMemo(() => {
    return data?.pages.flatMap((p) => p.products) ?? [];
  }, [data]);

  const { data: categories = [] } = useQuery({
    queryKey: ["admin-categories"],
    queryFn: () => apiFetch<Category[]>("/api/admin/categories"),
  });



  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      apiFetch(`/api/admin/products/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-products"] });
    },
  });

  const discountMutation = useMutation({
    mutationFn: (data: { productIds: number[]; percentage: number; roundTo: string }) =>
      apiFetch("/api/admin/products/bulk-discount", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-products"] });
      setSelected(new Set());
      setShowDiscount(false);
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: ({ id, body }: { id: number; body: DuplicateProductOptions }) =>
      apiFetch<{ id: number }>(`/api/admin/products/${id}/duplicate`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["admin-products"] });
      setDuplicateOpen(false);
      setMobileDuplicateProduct(null);
      setSelected(new Set());
      navigate({ to: "/admin/products/$id/edit", params: { id: String(data.id) } });
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (productIds: number[]) =>
      apiFetch<{ ok: boolean; deleted: number }>("/api/admin/products/bulk-delete", {
        method: "POST",
        body: JSON.stringify({ productIds }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-products"] });
      setSelected(new Set());
    },
  });

  const printSkuMutation = useMutation({
    mutationFn: ({ text, quantity }: { text: string; quantity: number; productId: number }) =>
      apiFetch("/api/admin/print-sku", {
        method: "POST",
        body: JSON.stringify({ text, quantity }),
      }),
    onSuccess: (_data, variables) => {
      setPrintMessage({
        type: "success",
        text: `Štampano ${variables.quantity}× ${variables.text}`,
      });
      setTimeout(() => setPrintMessage(null), 3000);
    },
    onError: (error: Error) => {
      setPrintMessage({ type: "error", text: error.message });
      setTimeout(() => setPrintMessage(null), 5000);
    },
  });

  const singleSelectedId = selected.size === 1 ? Array.from(selected)[0] : null;
  const singleSelectedProduct = singleSelectedId != null
    ? products.find((p) => p.id === singleSelectedId)
    : null;
  const productToDuplicate = mobileDuplicateProduct ?? singleSelectedProduct;

  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  function openDuplicateDialog(product?: Product) {
    setDuplicateOptions(defaultDuplicateFormState());
    setMobileDuplicateProduct(product ?? null);
    setDuplicateOpen(true);
  }

  function setDuplicateField(key: keyof DuplicateFormState, value: boolean) {
    setDuplicateOptions((prev) => ({ ...prev, [key]: value }));
  }

  function handleConfirmDuplicate() {
    const id = mobileDuplicateProduct?.id ?? singleSelectedId;
    if (id == null) return;
    duplicateMutation.mutate({ id, body: duplicateOptions });
  }

  function handleDelete(id: number, name: string) {
    setDeleteTarget({ id, name });
    setDeleteConfirmOpen(true);
  }

  function handlePrintSku(product: Product, quantity: number) {
    if (!product.sku) return;
    printSkuMutation.mutate({
      text: product.sku,
      quantity,
      productId: product.id,
    });
  }

  function handleConfirmDelete() {
    if (deleteTarget == null) return;
    deleteMutation.mutate(deleteTarget.id);
    setDeleteConfirmOpen(false);
    setDeleteTarget(null);
  }

  function handleBulkDelete() {
    if (selected.size === 0) return;
    const n = selected.size;
    if (
      !confirm(
        n === 1
          ? "Obrisati odabrani proizvod? Ova radnja se ne može poništiti."
          : `Obrisati ${n} proizvoda? Ova radnja se ne može poništiti.`
      )
    ) {
      return;
    }
    bulkDeleteMutation.mutate(Array.from(selected));
  }

  function toggleSelect(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (selected.size === products.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(products.map((p) => p.id)));
    }
  }

  function handleApplyDiscount() {
    const pct = parseFloat(discountPercent);
    if (!pct || pct <= 0 || pct >= 100 || selected.size === 0) return;
    discountMutation.mutate({
      productIds: Array.from(selected),
      percentage: pct,
      roundTo,
    });
  }

  const previewDiscount = useMemo(() => {
    if (selected.size === 0) return null;
    const pct = parseFloat(discountPercent);
    if (!pct || pct <= 0 || pct >= 100) return null;
    const sample = products.find((p) => selected.has(p.id));
    if (!sample) return null;
    let discounted = sample.price * (1 - pct / 100);
    if (roundTo === "floor") discounted = Math.floor(discounted / 100) * 100;
    else if (roundTo === "ceil") discounted = Math.ceil(discounted / 100) * 100;
    else discounted = Math.round(discounted);
    return { original: sample.price, discounted, name: sample.name };
  }, [selected, discountPercent, roundTo, products]);

  const allSelected = products.length > 0 && selected.size === products.length;
  const someSelected = selected.size > 0;

  return (
    <div className="flex flex-col gap-4 sm:gap-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl sm:text-2xl font-bold">Proizvodi</h1>
        <div className="flex items-center gap-2">
          {someSelected && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowDiscount(!showDiscount)}
              className="hidden md:inline-flex"
            >
              <Percent className="size-4 mr-1.5" />
              Popust ({selected.size})
            </Button>
          )}
          {someSelected && (
            <Button
              size="sm"
              variant="outline"
              className="text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive shrink-0"
              onClick={handleBulkDelete}
              disabled={bulkDeleteMutation.isPending}
            >
              <Trash2 className="size-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Obriši ({selected.size})</span>
              <span className="sm:hidden">Obriši</span>
            </Button>
          )}
          {singleSelectedId != null && singleSelectedProduct?.sku && (
            <div className="hidden sm:flex items-center shrink-0">
              <StickerPrintControls
                compact
                quantity={headerPrintQuantity}
                onQuantityChange={setHeaderPrintQuantity}
                onPrint={() => handlePrintSku(singleSelectedProduct, headerPrintQuantity)}
                canPrint
                isPrinting={printSkuMutation.isPending && printSkuMutation.variables?.productId === singleSelectedProduct.id}
              />
            </div>
          )}
          {singleSelectedId != null && (
            <Button size="sm" variant="outline" onClick={() => openDuplicateDialog()} className="shrink-0">
              <Copy className="size-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Dupliraj</span>
            </Button>
          )}
          <Button asChild size="sm">
            <Link to="/admin/products/new">
              <Plus className="size-4 mr-1.5" />
              <span className="hidden sm:inline">Dodaj proizvod</span>
              <span className="sm:hidden">Dodaj</span>
            </Link>
          </Button>
        </div>
      </div>

      {showDiscount && someSelected && (
        <BulkDiscountPanel
          selectedCount={selected.size}
          discountPercent={discountPercent}
          onDiscountPercentChange={setDiscountPercent}
          roundTo={roundTo}
          onRoundToChange={setRoundTo}
          preview={previewDiscount}
          onApply={handleApplyDiscount}
          onClose={() => setShowDiscount(false)}
          isPending={discountMutation.isPending}
        />
      )}

      <ProductsSearch
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        categoryFilter={categoryFilter}
        onCategoryChange={setCategoryFilter}
        categories={categories}
        inStockOnly={inStockOnly}
        onInStockOnlyChange={setInStockOnly}
        onOpenScanner={() => setScannerOpen(true)}
      />

      {printMessage && (
        <p className={`text-sm ${printMessage.type === "error" ? "text-destructive" : "text-green-600"}`}>
          {printMessage.text}
        </p>
      )}

      {isLoading ? (
        <p className="text-muted-foreground text-sm">Učitavanje...</p>
      ) : isError ? (
        <div className="flex flex-col items-center gap-3 py-8">
          <p className="text-destructive text-sm">Došlo je do greške pri učitavanju proizvoda.</p>
          <Button size="sm" variant="outline" onClick={() => refetch()}>
            Pokušaj ponovo
          </Button>
        </div>
      ) : products.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {debouncedSearch || categoryFilter !== "all" || inStockOnly
            ? "Nema rezultata pretrage."
            : "Nema proizvoda."}
        </p>
      ) : (
        <>
          <ProductsTable
            products={products}
            selected={selected}
            onToggleSelect={toggleSelect}
            onToggleSelectAll={toggleSelectAll}
            allSelected={allSelected}
            someSelected={someSelected}
            onDuplicate={openDuplicateDialog}
            onDelete={handleDelete}
            onPrintSku={handlePrintSku}
            printingProductId={
              printSkuMutation.isPending ? (printSkuMutation.variables?.productId ?? null) : null
            }
            isDeletePending={deleteMutation.isPending}
          />
          <div ref={sentinelRef} className="flex justify-center py-4">
            {isFetchingNextPage ? (
              <p className="text-muted-foreground text-sm">Učitavanje...</p>
            ) : !hasNextPage ? (
              <p className="text-muted-foreground text-sm">Svi proizvodi učitani.</p>
            ) : null}
          </div>
        </>
      )}

      <DuplicateProductDialog
        open={duplicateOpen}
        onOpenChange={(open) => {
          setDuplicateOpen(open);
          if (!open) setMobileDuplicateProduct(null);
        }}
        options={duplicateOptions}
        onOptionChange={setDuplicateField}
        productName={productToDuplicate?.name ?? null}
        productSku={productToDuplicate?.sku ?? null}
        onConfirm={handleConfirmDuplicate}
        isPending={duplicateMutation.isPending}
      />

      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={(open) => {
          setDeleteConfirmOpen(open);
          if (!open) setDeleteTarget(null);
        }}
        itemName={deleteTarget?.name ?? ""}
        onConfirm={handleConfirmDelete}
        isPending={deleteMutation.isPending}
      />

      <BarcodeScanner
        open={scannerOpen}
        onScan={(text) => { setSearchQuery(text); setScannerOpen(false); }}
        onClose={() => setScannerOpen(false)}
        title="Skeniraj proizvod"
        hint="Skenirajte barkod za pretragu proizvoda"
        scannerId="products-barcode-reader"
        error={scannerError}
      />
    </div>
  );
}
